/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWtrSelector } from 'redux/hooks';
import classNames from 'classnames';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import FocusTrap from 'components/FocusTrap';
import { BuilderVariant } from '../types';

import styles from './ProductSelectMobileScreen.scss';
import { getGroupNameByIndex } from '../selectors';

export interface ProductSelectMobileScreenProps {
  children: React.ReactNode;
  groupId: string;
  groupIndex: number;
  mobileHeader?: React.ReactElement<any> | null;
  onClickBack: () => void;
  onDismiss: () => void;
  selectedGroup: string | null;
  initialMessage: string;
  promotionId?: string;
  variant: BuilderVariant;
}

const lockBody = (val: boolean = true) => {
  document.querySelector('body')?.style.setProperty('overflow', val ? 'hidden' : '');
};

const ProductSelectMobileScreen = ({
  children,
  groupId,
  groupIndex,
  initialMessage,
  mobileHeader = null,
  onClickBack,
  onDismiss,
  promotionId,
  selectedGroup,
  variant,
}: ProductSelectMobileScreenProps) => {
  const groupName = useWtrSelector(state =>
    getGroupNameByIndex(state, { promotionId, index: 0, variant, groupIndex }),
  );
  const isOpen = selectedGroup === groupId;
  const ref = useRef<HTMLDivElement>(null);
  const titleText = groupName || initialMessage;
  const backLinkRef = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);

  const dismiss = useCallback(() => {
    setActive(false);
    lockBody(false);
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActive(true);
      lockBody(true);

      const timerId = setTimeout(() => setOpen(true));

      return () => {
        clearTimeout(timerId);
      };
    }

    const screenRef = ref.current;

    if (screenRef) {
      setOpen(false);

      screenRef.addEventListener('transitionend', dismiss, { once: true });

      return () => {
        screenRef.removeEventListener('transitionend', dismiss);
      };
    }

    return () => {};
  }, [dismiss, isOpen]);

  useEffect(() => () => lockBody(false), []);

  return (
    <FocusTrap active={active}>
      <div
        data-testid="product-select-mobile-screen"
        className={classNames(styles.screen, {
          [styles.open]: open,
          [styles.active]: active,
        })}
        ref={ref}
      >
        <header className={styles.topbar}>
          <div className={styles.headerTop}>
            <TextLink
              className={styles.backButton}
              iconLeft
              underline="never"
              component="button"
              onClick={onClickBack}
              ref={backLinkRef}
            >
              Back
            </TextLink>
            <Typography styleAs="paragraphHeading" element="h2" noMargins>
              {titleText}
            </Typography>
          </div>
          {mobileHeader}
        </header>
        <div className={styles.content}>{isOpen ? children : null}</div>
      </div>
    </FocusTrap>
  );
};

ProductSelectMobileScreen.displayName = 'ProductSelectMobileScreen';

export default ProductSelectMobileScreen;
